import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormHelperText,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Badge,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useState } from "react";
import {
  CustomTabPanel,
  CustomeDialog,
  InputControl,
  Loader,
  PageListLayout,
} from "../../../shared";
import { styled } from "@mui/material/styles";
import { useProductionChart } from "../hooks/useProductionChart";
import { ProductionChartRecipeDetails } from "../components/ProductionChartRecipeDetails";
import { EditProductionChart } from "../components/EditProductionChart";
import { ProductionChartTemplateDialog } from "../components/ProductionChartTemplateDialog";
import { BatchListCard } from "../components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment/moment";
import { CustomDialogTemplate } from "../../../shared/components/CustomDialogTemplate";
import SimpleReactValidator from "simple-react-validator";
import { navigateBasedOnUserType } from "../../../utils";
import BatchProductList from "../components/BatchProductList";
import BatchIngredientList from "../components/BatchIngredientList";
import emptyIcon from "../../../assets/images/Icons/search-table.png";
import Error_info_icon from "../../../assets/images/Icons/ic_info.svg";
import { ProductionChartErrorMessageDialog } from "../components/ProductionChartErrorMessageDialog";
import CustomDialogFooter from "../../../shared/components/CustomDialogFooter";

const StyledBadge = styled(Badge)(({ active }) => ({
  "& .MuiBadge-badge": {
    right: 4,
    top: 10,
    padding: "0 4px",
    position: "relative",
    background: active ? "#FFFFFF" : "#263141",
    color: active ? "#263141" : "#FFFFFF",
  },
}));

export const ProductionChartDetails = () => {
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const [, forceUpdate] = React.useState();

  const validator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      validators: {
        Item: {
          message: "The recipes field is required.",
          required: true,
          rule: (value) => {
            if (value?.length <= 0) return false;
            else return true;
          },
        },
      },
    }),
  );
  const { date } = useParams();
  const navigate = useNavigate();
  const {
    productionChartDetails: { data },
    editTemplate,
    handleModal,
    productionchart,
    handleTemplateModal,
    handleSelectStore,
    onSearchChange,
    categorylistQuery,
    handleCustomTableChange,
    handleRecipes,
    onChangeFilter,
    onTemplateSearchChange,
    updateTableData,
    onStoreChange,
    onUpdate,
    handleTemplateChange,
    convertedData,
    isEditable,
    handleCustomFieldChange,
    recipelistQuery,
    handleSelect,
    handleRecipeSelect,
    globalData,
    handleShowErrorMessage,
    handleProductionChartDownload,
    setProductionchart,
    selectedIndex,
    handleBatchSplitUpdate,
    editedMap,
    setEditedMap,
    setFocusedField,
    focusedField,
    updateProductionchartBatchSplit,
    handleAddBatch,
    handleDeleteBatch,
    handleDeleteBatchDialog,
    setSelectedIndex,
    downloadProductionChart,
    hasAnyBatchError,
    hasLastBatchWeightZero,
    ingredientListRef,
    productListRef,
    onDownloadPCByCategoryId,
    downloadProductionChartByCategory,
    toggleAllProducts,
    setToggleAllProducts,
    resetRegenerateSearch,
  } = useProductionChart({
    detId: moment(date)?.format("YYYY-MM-DD"),
  });

  const title = data?.date ? moment(data?.date).format("DD-MM-YYYY") : "";

  const ExceedBowlCapacity = data?.stores?.find(
    (i) => i.store_id === productionchart?.selectedStore?.store_id,
  );

  const { selectedStore, isLoading } = productionchart;

  const optionsStoreList = data?.stores?.map((item) => ({
    name: item.store_name,
    id: item.store_id,
  }));

  const ColorButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#F5F8FC",
    color: "#111C2B", // Darker text for better contrast
    width: "87px",
    height: "36px",
    border: "1px solid #D3DCE6", // Light gray border
    borderRadius: "8px", // Soft rounded corners
    textTransform: "none", // Avoid uppercase text
    fontSize: "14px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#E9EFF6", // Slight hover effect
      borderColor: "#B0BEC5",
    },
  }));

  const [value, setValue] = useState(0);

  const handleChange = (e, v) => {
    setValue(v);
  };

  const onUpdateSubmit = (type) => {
    if (validator.current.allValid()) {
      onUpdate(type);
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  // Accumulate the total other recipe count
  const getTotalRecipes = (recipes = [], reduce = false) => {
    return reduce
      ? recipes.reduce((acc, option) => option.recipes.length + acc, 0)
      : recipes.length;
  };

  const batchesLength = selectedStore?.batches?.length || 0;

  const isCategoryBasedOtherRecipes =
    selectedStore?.other_recipes?.length > 0 &&
    Array.isArray(selectedStore?.other_recipes[0]?.recipes);

  const recipesLength = getTotalRecipes(
    selectedStore?.other_recipes,
    isCategoryBasedOtherRecipes,
  );

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) =>
        navigateBasedOnUserType("/production-charts", isStoreAdmin, navigate)
      }
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Production Chart
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {title}
    </Typography>,
  ];

  const IngredientItem = ({ item, level = 0 }) => {
    const [expanded, setExpanded] = useState(false);
    const hasSubItems = item.type === "recipe";

    const handleExpand = () => {
      setExpanded(!expanded);
    };

    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: level === 0 ? "#F5F7FA" : "transparent",
            borderRadius: "1px",
            padding: "12px 10px",
            paddingLeft: `${level === 0 ? "" : level * 20}px`,
            minWidth: `500px`,
            color: "#333",
            mt: 0.5,
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2">{item.name}</Typography>
            {hasSubItems && (
              <IconButton size="small" onClick={handleExpand}>
                <ExpandMoreIcon
                  sx={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">{item.pivot.base_unit_qty}</Typography>
            <Typography variant="body2">
              {item.pivot.base_unit_symbol}
            </Typography>
          </Box>
        </Box>

        {hasSubItems && expanded && (
          <Box>
            {item.ingredient.map((subItem, index) => (
              <IngredientItem key={index} item={subItem} level={level + 1} />
            ))}
          </Box>
        )}
      </>
    );
  };

  const batchIndex =
    productionchart.selectedBatchIndex === null
      ? 0
      : productionchart.selectedBatchIndex;

  // Filter batches based on the selected priority
  const filteredBatches = selectedStore?.batches?.filter(
    (batch) => batch.priority === productionchart.selectedBatchPriority,
  );

  // Select the current batch from the filtered list
  const currentBatch =
    filteredBatches && filteredBatches?.length > 0
      ? filteredBatches[batchIndex]
      : null;

  return (
    <PageListLayout
      headerText={
        <Stack direction="row" alignItems="center" gap="12px">
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "24px",
            }}
          >
            {isLoading ? "Loading..." : title}
          </Typography>
          <Chip
            size="small"
            variant={data?.status ? "contained" : "generated"}
            color={data?.status ? "primary" : undefined}
            label={data?.status ? "COMPLETED" : "GENERATED"}
          />
        </Stack>
      }
      breadcrumbs={breadcrumbs}
      rightContent={
        <Stack direction="row" gap="12px" flexWrap="wrap">
          {!isStoreAdmin && (
            <Autocomplete
              size="small"
              options={data?.stores || []}
              disableClearable //  Prevents the close icon from appearing
              clearIcon={null} //  Ensures no clear icon is displayed
              value={
                (data?.stores?.length > 0 &&
                  productionchart?.selectedStore?.store_id &&
                  data?.stores?.find(
                    (c) =>
                      c.store_id === productionchart?.selectedStore?.store_id,
                  )) ||
                null
              }
              getOptionLabel={(option) => option?.store_name || ""}
              onChange={(event, option) => handleSelectStore(option)}
              isOptionEqualToValue={(option, value) =>
                option?.store_id === value?.store_id
              }
              sx={{
                width: "230px",
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Stores"
                  variant="outlined"
                />
              )}
            />
          )}

          <Button
            variant="outlined"
            color="gray2"
            onClick={() => handleTemplateModal(true, 0)}
          >
            Template
          </Button>

          {isEditable && !data?.status ? (
            <Button
              variant="outlined"
              color="gray2"
              onClick={() => handleModal(true, 0)}
            >
              Regenerate Batch
            </Button>
          ) : (
            ""
          )}
        </Stack>
      }
      tabs={
        <Stack
          mb="6px"
          alignItems="center"
          flexDirection="row"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Tabs className="tabs" value={value} onChange={handleChange}>
            <Tab
              label={
                <Stack direction="row" gap="4px">
                  Batches
                  <StyledBadge
                    badgeContent={batchesLength}
                    active={value === 0}
                  />
                </Stack>
              }
            />
            <Tab
              label={
                <Stack direction="row" gap="10px">
                  Products
                  <StyledBadge
                    badgeContent={recipesLength}
                    active={value === 1}
                  />
                </Stack>
              }
            />
          </Tabs>

          <Stack
            display="flex"
            alignItems="center"
            direction="row"
            gap="8px"
            ml="auto"
          >
            {ExceedBowlCapacity?.exceeds_Bowl_Capacity?.length ? (
              <React.Fragment>
                <Typography
                  color="textSecondary"
                  fontWeight={600}
                  fontSize="14px"
                >
                  Last generated on:{" "}
                  {selectedStore?.lastgenerated
                    ? moment(selectedStore?.lastgenerated).format(
                        "DD MMM YYYY hh:mm A",
                      )
                    : "-"}
                </Typography>
                <IconButton
                  color="error"
                  onClick={() => handleShowErrorMessage(true)}
                >
                  <img src={Error_info_icon} alt="info" />
                </IconButton>
              </React.Fragment>
            ) : (
              <Typography
                fontWeight={600}
                fontSize="14px"
                color="textSecondary"
                marginRight={1}
              >
                Last generated successfully on:{" "}
                {selectedStore?.lastgenerated
                  ? moment(selectedStore?.lastgenerated).format(
                      "DD MMM YYYY hh:mm A",
                    )
                  : "-"}
              </Typography>
            )}
            <Divider orientation="vertical" variant="middle" flexItem />
            <IconButton
              onClick={handleProductionChartDownload}
              disabled={downloadProductionChart?.isPending}
            >
              {downloadProductionChart?.isPending ? (
                <CircularProgress color="primary" size="20px" />
              ) : (
                <img
                  alt="details"
                  src="/icons/ic_print.png"
                  style={{
                    height: "20px",
                  }}
                />
              )}
            </IconButton>
          </Stack>
        </Stack>
      }
      sx={{
        backgroundColor: "background.paper",
        p: 0,
        height: "calc(100vh - 154px)",
        overflowY: "auto",
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            position: "relative",
            top: "50%",
            transform: "translateY(-48px)",
          }}
        >
          <Loader />
        </Box>
      ) : (
        <>
          <CustomTabPanel value={value} index={0} border={false}>
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
              }}
            >
              {/* Action Grid */}
              <Stack
                p="8px"
                sx={{
                  backgroundColor: "background.default",
                  width: { md: "290px", xs: "100%", transition: "width 0.5s" },
                  height: "calc(100vh - 154px)",
                  overflowY: "auto",
                }}
              >
                <BatchListCard
                  data={selectedStore?.batches}
                  state={productionchart}
                  handleSelect={handleSelect}
                  globalData={globalData}
                />
              </Stack>

              {/* View Grid */}
              <Box flex={1}>
                <Stack
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    width: "100%",
                    minHeight: "calc(100dvh - 154px)",
                  }}
                >
                  {selectedStore?.batches?.length ? (
                    <React.Fragment>
                      <Box
                        ref={productListRef}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          minWidth: { lg: "345px" },
                          flexGrow: 345,
                          flex: 1,
                          px: "12px",
                          gap: "12px",
                          pb: "12px",
                          borderRight: { sm: 1 },
                          borderColor: { sm: "divider" },
                          height: "calc(100vh - 154px)",
                          overflowY: "auto",
                        }}
                      >
                        <Box pt="16px">
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: "16px",
                            }}
                          >
                            Batch is for
                          </Typography>
                        </Box>
                        <BatchProductList
                          products={currentBatch?.products ?? []}
                          globalData={globalData}
                        />
                      </Box>

                      <Box
                        ref={ingredientListRef}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          px: "12px",
                          gap: "12px",
                          flex: 2,
                          pb: "12px",
                          height: "calc(100vh - 154px)",
                          overflowY: "auto",
                        }}
                      >
                        <Box pt="16px">
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: "16px",
                            }}
                          >
                            Ingredients
                          </Typography>
                        </Box>

                        <BatchIngredientList
                          currentBatch={currentBatch}
                          selectedStore={selectedStore?.batches[batchIndex]}
                          batchIndex={batchIndex}
                          globalData={globalData}
                        />
                      </Box>
                    </React.Fragment>
                  ) : (
                    <Stack
                      sx={{
                        flex: 1,
                        flexShrink: 1,
                        height: "calc(100dvh - 145px)",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img src={emptyIcon} alt="empty" height={56} width={56} />
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "14px",
                          color: "#485364",
                          width: "500px",
                        }}
                      >
                        There are no products or ingredients yet!. You need to
                        create batch first.
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Stack>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1} border={false}>
            <ProductionChartRecipeDetails
              data={selectedStore?.other_recipes}
              selectedStore={selectedStore}
              productionchart={productionchart}
              isStoreAdmin={isStoreAdmin}
              handleRecipeSelect={handleRecipeSelect}
              onDownloadPCByCategoryId={onDownloadPCByCategoryId}
              downloadProductionChartByCategory={
                downloadProductionChartByCategory
              }
              globalData={globalData}
              toggleAllProducts={toggleAllProducts}
              setToggleAllProducts={setToggleAllProducts}
            />
          </CustomTabPanel>
        </>
      )}

      {/* Regenerate batch dialog */}
      <CustomDialogTemplate
        PaperProps={{
          sx: {
            minWidth: "80vw",
            height: "90vh",
          },
        }}
        closeIcon={false}
        open={productionchart.isOpen}
        title={
          productionchart?.isBowlcapaciytyExceed ? (
            "Adjust Product Quantities"
          ) : (
            <Stack direction="row" gap="12px">
              <Typography
                alignContent="center"
                width="100%"
                minWidth="180px"
                fontWeight={500}
              >
                {productionchart?.templateDate &&
                  moment(productionchart?.templateDate).format("DD-MM-YYYY")}
              </Typography>
              <InputControl
                label="Tax Group"
                sx={{
                  width: "100%",
                  minWidth: 230,
                }}
                type="dropdown"
                disableClearable
                required
                disabled={isStoreAdmin}
                options={optionsStoreList ?? []}
                getOptionLabel={(option) => option?.name}
                value={
                  (optionsStoreList?.length > 0 &&
                    productionchart?.store &&
                    optionsStoreList?.find(
                      (c) => c.id === productionchart?.store,
                    )) ||
                  null
                }
                onChange={(e, newValue) => {
                  onStoreChange({
                    target: {
                      name: "store",
                      value: newValue?.id,
                      index: 0,
                    },
                    data: data?.stores,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Stores"
                    required
                    error={validator.current.message(
                      "Store",
                      productionchart?.store,
                      "required",
                    )}
                    helperText={validator.current.message(
                      "Store",
                      productionchart?.store,
                      "required",
                    )}
                    size="small"
                  />
                )}
              />
            </Stack>
          )
        }
        middleContent={
          <Stack backgroundColor="background.default" px="16px">
            <Tabs value={productionchart.temp_index}>
              {productionchart?.isBowlcapaciytyExceed
                ? productionchart?.batchTemplateDetails?.templates?.map(
                    (i, index) => {
                      return (
                        <Tab
                          key={index}
                          label={i?.name}
                          onClick={() => {
                            handleTemplateChange(index, i?.id, i.recipeList);
                          }}
                        />
                      );
                    },
                  )
                : productionchart?.dataList?.map((i, index) => {
                    return (
                      <Tab
                        key={index}
                        label={i?.template_name}
                        onClick={() => {
                          handleTemplateChange(
                            index,
                            i?.template_id,
                            i.recipeList,
                          );
                        }}
                      />
                    );
                  })}
            </Tabs>
          </Stack>
        }
        content={
          <EditProductionChart
            handleCustomTableChange={handleCustomTableChange}
            productionchart={productionchart}
            categorylistQuery={categorylistQuery}
            handleRecipes={handleRecipes}
            onChangeFilter={onChangeFilter}
            updateTableData={updateTableData}
            onSearchChange={onTemplateSearchChange}
            handleCustomFieldChange={handleCustomFieldChange}
            recipelistQuery={recipelistQuery}
            setProductionchart={setProductionchart}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            handleBatchSplitUpdate={handleBatchSplitUpdate}
            editedMap={editedMap}
            setEditedMap={setEditedMap}
            setFocusedField={setFocusedField}
            focusedField={focusedField}
            updateProductionchartBatchSplit={updateProductionchartBatchSplit}
            handleAddBatch={handleAddBatch}
            handleDeleteBatch={handleDeleteBatch}
            handleDeleteBatchDialog={handleDeleteBatchDialog}
            hasAnyBatchError={hasAnyBatchError}
            resetRegenerateSearch={resetRegenerateSearch}
            globalData={globalData}
          />
        }
        tableFooter={
          !productionchart?.isBowlcapaciytyExceed ? (
            <Stack
              direction="row"
              justifyContent="flex-end"
              gap="8px"
              padding="10px"
            >
              <Box sx={{ alignContent: "center" }}>
                <FormHelperText sx={{ color: "#C60808" }}>
                  {validator.current.message(
                    "Recipe field",
                    convertedData,
                    "Item",
                  )}
                </FormHelperText>
              </Box>
              <Button
                variant="outlined"
                color="gray2"
                onClick={() => handleModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={editTemplate?.isPending}
                onClick={() => onUpdateSubmit("initial")}
                startIcon={
                  editTemplate?.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                Regenerate Batches
              </Button>
            </Stack>
          ) : (
            <CustomDialogFooter
              sx={{ p: "12px", gap: "12px" }}
              handleClose={() => handleModal(false)}
              onSubmit={() => {
                hasLastBatchWeightZero({
                  productionchart,
                  type: "secondary",
                });
              }}
              disabled={
                editTemplate?.isPending || hasAnyBatchError(productionchart)
              }
              loading={editTemplate?.isPending}
              submitButton="Proceed"
            />
          )
        }
      />

      <CustomeDialog
        closeIcon={false}
        open={productionchart.isTemplateOpen}
        handleClose={(e) => {
          handleTemplateModal(false);
        }}
        isHeader={false}
        content={
          <ProductionChartTemplateDialog
            handleModal={handleTemplateModal}
            ColorButton={ColorButton}
            templateList={productionchart.tempateList}
            onSearchChange={onSearchChange}
          />
        }
        PaperProps={{
          sx: {
            width: { md: "650px", sm: "500px", xs: "90%" },
          },
        }}
        sx={{
          height: "auto",
          minHeight: "60vh",
          "&:hover": {
            overflowY: "auto",
          },
        }}
        footer={{
          display: true,
          onCancel: () => handleTemplateModal(false),
        }}
      />

      <ProductionChartErrorMessageDialog
        open={productionchart?.isShowErrorMessage}
        handleClose={handleShowErrorMessage}
        state={ExceedBowlCapacity?.exceeds_Bowl_Capacity ?? []}
      />
    </PageListLayout>
  );
};
